import {
  Tyres,
  type Configuration,
  EmissionsConsumption,
} from '@tribal-worldwide-london/fa-configuration-service-vwg';
import type {
  PdbLong,
  PdbShort,
} from '@tribal-worldwide-london/fa-pdb-service-vwg';
import { SpecOption, type TechnologyItem } from '@tribal-worldwide-london/fa-product-service-vwg';
import { type Retailers } from '@tribal-worldwide-london/fa-retailer-service-vwg';
import { StockSpecOption, type StockConfiguration } from '@tribal-worldwide-london/fa-stock-service-vwg';
import { produce } from 'immer';
import { createWithEqualityFn as create } from 'zustand/traditional';
import { ArrayElement } from '../types';

export type ConfigurationType =
  | 'pdbData'
  | 'productData'
  | 'configData'
  | 'stockData';

export type ProductDataKeys = 'standard' | 'liked' | 'extras';

export type ProductData = {
  technologyItems: Record<ProductDataKeys, TechnologyItem[]>;
  options: SpecOption[];
};

export type StockData = ConfigDataWithInfo<StockConfiguration> & {
  options: StockSpecOption[];
  // Unwrapped `/stock/{vin}/derivative/extra` payload. VIN PDBs omit battery.
  derivativeExtra?: {
    battery?: { capacity?: number | null } | null;
  };
};

export type ConfigurationKey = 'fullUrl' | 'marketingShortCode' | 'orderNumber' | 'vin';

export type ConfigDataWithInfo<T> = T & {
  tyreInformationData?: Tyres;
  emissionsConsumptionData?: EmissionsConsumption;
};

export type Configurations = {
  [key: string]:
    | PdbLong
    | ProductData
    | ConfigDataWithInfo<Configuration>
    | StockData;
};

export type GlobalStoreStateConfiguration = ArrayElement<
  PdbShort['configurations']
> & {
  vin?: string;
  pdbData?: PdbLong;
  productData?: ProductData;
  configData?: ConfigDataWithInfo<Configuration>;
  stockData?: StockData;
};

export interface GlobalStoreState {
  activeSlideIndex: number;
  app: Partial<Omit<PdbShort, 'configurations'>> & {
    retailers: Retailers[];
    configurations: GlobalStoreStateConfiguration[];
    activeConfiguration?: number;
  };
}

export type GlobalStoreAction = {
  setActiveSlideIndex: (index: number) => void;
  setInitialAppData: (data: GlobalStoreState['app']) => void;
  setAppData: (data: PdbShort) => void;
  setAppRetailersData: (data: Retailers[]) => void;
  setActiveConfiguration: (index: number) => void;
  addConfigurationDataByKey: (
    data: Configurations,
    key: ConfigurationKey,
    type: ConfigurationType
  ) => void;
};

const useGlobalStore = create<GlobalStoreState & GlobalStoreAction>((set) => ({
  app: {
    retailers: [],
    configurations: [],
    activeConfiguration: undefined,
  },
  activeSlideIndex: 0,
  setActiveSlideIndex: (activeSlideIndex: number) =>
    set((state) =>
      produce(state, (state) => {
        state.activeSlideIndex = activeSlideIndex;
      })
    ),
  setInitialAppData: (app: GlobalStoreState['app']) => set(() => ({ app })),
  setAppData: (app: PdbShort) =>
    set(() => ({
      app: { ...app, retailers: [], activeConfiguration: undefined },
    })),
  setAppRetailersData: (retailers: Retailers[]) =>
    set((state) => ({ app: { ...state.app, retailers } })),
  addConfigurationDataByKey: (
    data: Configurations,
    key: ConfigurationKey,
    type: ConfigurationType
  ) =>
    set((state) => {
      
      if (state.app.configurations) {
        return produce(state, ({ app }: GlobalStoreState) => {
          app.configurations = app.configurations.map((configuration) => {
            const value = configuration[key];
            if (value) {
              if (type === 'pdbData') {
                configuration[type] = data[value] as PdbLong;
              } else if (type === 'configData') {
                configuration[type] = data[
                  value
                ] as ConfigDataWithInfo<Configuration>;
              } else if (type === 'stockData') {
                configuration[type] = data[value] as StockData;
              } else if (type === 'productData') {
                configuration[type] = data[value] as ProductData;
              }
            }
            return configuration;
          });
        });
      }

      return state;
    }),
  setActiveConfiguration: (index: number) => {
    return set((state) => ({
      app: { ...state.app, activeConfiguration: index },
    }));
  },
}));

export default useGlobalStore;
